// Phase 22B — DA Rates admin (Role × Market Type × Amount)
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Wallet } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const TA_DA_ROLES = ["SE", "ASM", "RSM", "ZSM", "SALES_HEAD"];

export default function DaRates() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markets, setMarkets] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/da-rates", { params: { page_size: 200 } });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      const [m, r] = await Promise.all([
        api.get("/lookups?category=market_type"),
        api.get("/roles"),
      ]);
      setMarkets((m.data.items || m.data || []).filter(x => x.is_active !== false));
      const allRoles = (r.data.items || r.data || []);
      setRoles(allRoles.filter(x => TA_DA_ROLES.includes(x.code)));
    })().catch(e => toast.error(formatApiError(e)));
    load();
  }, [load]);

  const roleName = (code) => roles.find(r => r.code === code)?.name || code;
  const marketLabel = (code) => markets.find(m => m.code === code)?.label || code;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="da-rates-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "DA Rates" }]}
        title="DA Rates"
        subtitle="Daily allowance per role × market type — legacy base for policies without an override."
        favorite={{ key: "admin.da-rates", type: "screen", label: "DA Rates", route: "/admin/da-rates" }}
        testid="darates-page-header"
      />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <Wallet className="h-6 w-6 text-[#6D2C91]" /> DA Rates
          </h1>
          <p className="text-xs text-neutral-500 mt-0.5 max-w-xl">
            Daily allowance amounts per role × market type. Auto-inserted onto every TA/DA claim
            based on the user's role and the attendance's <code>market_type_code</code>.
          </p>
        </div>
        <Button className="tulsi-gradient text-white" onClick={() => setEditing("new")}
                data-testid="da-rate-new">
          <Plus className="h-4 w-4 mr-1" /> New rate
        </Button>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-0">
          <Table data-testid="da-rates-table">
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Market</TableHead>
                <TableHead className="text-right">Amount (INR)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></TableCell></TableRow>}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-neutral-400 text-sm">No rates configured yet.</TableCell></TableRow>
              )}
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`da-rate-row-${r.id}`}>
                  <TableCell className="font-medium">{roleName(r.role_code)}
                    <div className="text-[10px] font-mono text-neutral-400 mt-0.5">{r.role_code}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-[#6D2C91]/10 text-[#6D2C91] border-[#6D2C91]/20">
                      {marketLabel(r.market_type_code)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    ₹ {(r.amount_inr || 0).toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell>
                    <Badge className={r.is_active ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-500"}>
                      {r.is_active ? "active" : "inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => setEditing(r)}
                            data-testid={`da-rate-edit-${r.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setConfirmDel(r)}
                            data-testid={`da-rate-delete-${r.id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && (
        <RateDialog editing={editing} roles={roles} markets={markets}
                     onClose={() => setEditing(null)}
                     onSaved={() => { setEditing(null); load(); }} />
      )}

      <Dialog open={!!confirmDel} onOpenChange={v => !v && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Remove DA rate?</DialogTitle>
            <DialogDescription>New claims for this role × market type will fail until you add a replacement.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700"
                    onClick={async () => {
                      try { await api.delete(`/da-rates/${confirmDel.id}`); toast.success("Removed"); setConfirmDel(null); load(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }}
                    data-testid="da-rate-delete-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function RateDialog({ editing, roles, markets, onClose, onSaved }) {
  const isNew = editing === "new";
  const [roleCode, setRoleCode] = useState(isNew ? "" : editing.role_code);
  const [marketCode, setMarketCode] = useState(isNew ? "" : editing.market_type_code);
  const [amount, setAmount] = useState(isNew ? "" : String(editing.amount_inr));
  const [isActive, setIsActive] = useState(isNew ? true : editing.is_active);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!roleCode || !marketCode || amount === "") { toast.error("All fields required"); return; }
    setSaving(true);
    try {
      const body = { role_code: roleCode, market_type_code: marketCode,
                     amount_inr: parseFloat(amount), is_active: isActive };
      if (isNew) await api.post("/da-rates", body);
      else await api.patch(`/da-rates/${editing.id}`, { amount_inr: parseFloat(amount), is_active: isActive });
      toast.success(isNew ? "DA rate created" : "DA rate updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="da-rate-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New" : "Edit"} DA rate</DialogTitle>
          <DialogDescription>Applies to a specific role × market type combination.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Role</Label>
            <Select value={roleCode} onValueChange={setRoleCode} disabled={!isNew}>
              <SelectTrigger data-testid="da-rate-role"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {roles.map(r => <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Market type</Label>
            <Select value={marketCode} onValueChange={setMarketCode} disabled={!isNew}>
              <SelectTrigger data-testid="da-rate-market"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {markets.map(m => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount (INR)</Label>
            <Input type="number" min="0" step="0.01" value={amount}
                   onChange={e => setAmount(e.target.value)}
                   data-testid="da-rate-amount" />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <Switch checked={isActive} onCheckedChange={setIsActive}
                    data-testid="da-rate-active" />
            <Label className="text-sm">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="tulsi-gradient text-white" onClick={save} disabled={saving}
                  data-testid="da-rate-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
